import React, { Component } from "react";
import { Button, Paper, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./ErrorBoundary.module.css";

export const withRouter = (Component) => {
    function Wrapper(props) {
        const navigate = useNavigate();
        const location = useLocation();

        return (
            <Component navigate={navigate} location={location} {...props} />
        );
    }

    return Wrapper;
};

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);

        this.state = {
            hasError: false,
            previousPathname: "",
        };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    static getDerivedStateFromProps(props, state) {
        if (props.location.pathname !== state.previousPathname) {
            return {
                hasError: false,
                previousPathname: props.location.pathname,
            };
        }

        return null;
    }

    componentDidCatch(error, errorInfo) {
        console.error("ErrorBoundary caught an error:", error, errorInfo);
    }

    handleRetry = () => {
        window.location.reload();
    };

    handleGoHome = () => {
        this.setState({ hasError: false });
        this.props.navigate("/");
    };

    render() {
        const { children } = this.props;
        const { hasError } = this.state;

        if (hasError) {
            return (
                <main
                    className={styles.ErrorBoundary}
                    role="alert"
                    aria-live="assertive"
                >
                    <Paper
                        className={styles.ErrorBoundaryCard}
                        elevation={0}
                    >
                        <Typography
                            variant="h4"
                            component="h1"
                            className={styles.ErrorBoundaryTitle}
                        >
                            Something went wrong
                        </Typography>

                        <Typography
                            variant="body1"
                            color="text.secondary"
                            className={styles.ErrorBoundaryDescription}
                        >
                            We couldn't load this page. Please try again, or return to the home page.
                        </Typography>

                        <div className={styles.ErrorBoundaryActions}>
                            <Button
                                variant="contained"
                                onClick={this.handleRetry}
                            >
                                Try again
                            </Button>

                            <Button
                                variant="outlined"
                                onClick={this.handleGoHome}
                            >
                                Go to home
                            </Button>
                        </div>
                    </Paper>
                </main>
            );
        }

        return children;
    }
}

export default withRouter(ErrorBoundary);
